import type { ApiNotifyException } from '../../api/types';
import type {
  ISettings, IThemeSettings, NotifyException,
  ThemeKey,
} from '../../types';
import type { GlobalState } from '../types';
import type { SharedState } from '../types/sharedState';

import { selectSharedSettings, selectSharedState } from '../selectors/sharedState';
import { updateSharedState } from './sharedState';
import { updateUserBlockedState } from './users';

export function replaceSettings<T extends GlobalState>(global: T, newSettings?: Partial<ISettings>): T {
  // Update global.settings.byKey
  const updatedGlobal = {
    ...global,
    settings: {
      ...global.settings,
      byKey: {
        ...global.settings.byKey,
        ...newSettings,
      },
    },
  };

  // Sync shared settings to sharedState
  if (newSettings) {
    const sharedKeys = [
      'animationLevel', 'messageSendKeyCombo', 'language',
      'timeFormat', 'wasTimeFormatSetManually', 'theme', 'shouldUseSystemTheme',
      'isConnectionStatusMinimized', 'canDisplayChatInTitle', 'shouldForceHttpTransport',
      'shouldAllowHttpTransport', 'shouldCollectDebugLogs', 'shouldDebugExportedSenders',
      'shouldWarnAboutSvg', 'shouldSkipWebAppCloseConfirmation', 'performance',
    ];

    const sharedUpdates: any = {};
    let hasSharedUpdates = false;

    for (const key of sharedKeys) {
      if (key in newSettings) {
        sharedUpdates[key] = (newSettings as any)[key];
        hasSharedUpdates = true;
      }
    }

    if (hasSharedUpdates) {
      return updateSharedState(updatedGlobal, {
        settings: {
          ...updatedGlobal.sharedState.settings,
          ...sharedUpdates,
        },
      });
    }
  }

  return updatedGlobal;
}

export function updateSharedSettings<T extends GlobalState>(
  global: T, newSettings?: Partial<SharedState['settings']>,
): T {
  const settings = selectSharedSettings(global);
  return updateSharedState(global, {
    settings: {
      ...settings,
      ...newSettings,
    },
  });
}

export function updateThemeSettings<T extends GlobalState>(
  global: T, theme: ThemeKey, newSettings?: Partial<IThemeSettings>,
): T {
  const current = global.settings.themes[theme];

  return {
    ...global,
    settings: {
      ...global.settings,
      themes: {
        ...global.settings.themes,
        [theme]: {
          ...current,
          ...newSettings,
        },
      },
    },
  };
}

export function addNotifyExceptions<T extends GlobalState>(
  global: T, notifyExceptions: ApiNotifyException[],
): T {
  notifyExceptions.forEach((notifyException) => {
    const { chatId, ...exceptionData } = notifyException;
    global = addNotifyException(global, chatId, exceptionData);
  });

  return global;
}

export function addNotifyException<T extends GlobalState>(
  global: T, id: string, notifyException: NotifyException,
): T {
  return {
    ...global,
    settings: {
      ...global.settings,
      notifyExceptions: {
        ...global.settings.notifyExceptions,
        [id]: notifyException,
      },
    },
  };
}

// eslint-disable-next-line consistent-return
export function updateNotifySettings<T extends GlobalState>(
  global: T, peerType: 'contact' | 'group' | 'broadcast', isSilent?: boolean, shouldShowPreviews?: boolean,
): T {
  switch (peerType) {
    case 'contact':
      return replaceSettings(global, {
        ...(typeof isSilent !== 'undefined' && { hasPrivateChatsNotifications: !isSilent }),
        ...(typeof shouldShowPreviews !== 'undefined' && { hasPrivateChatsMessagePreview: shouldShowPreviews }),
      });
    case 'group':
      return replaceSettings(global, {
        ...(typeof isSilent !== 'undefined' && { hasGroupNotifications: !isSilent }),
        ...(typeof shouldShowPreviews !== 'undefined' && { hasGroupMessagePreview: shouldShowPreviews }),
      });
    case 'broadcast':
      return replaceSettings(global, {
        ...(typeof isSilent !== 'undefined' && { hasBroadcastNotifications: !isSilent }),
        ...(typeof shouldShowPreviews !== 'undefined' && { hasBroadcastMessagePreview: shouldShowPreviews }),
      });
  }
}

export function addBlockedUser<T extends GlobalState>(global: T, contactId: string): T {
  global = updateUserBlockedState(global, contactId, true);

  return {
    ...global,
    blocked: {
      ...global.blocked,
      ids: [contactId, ...global.blocked.ids],
      totalCount: global.blocked.totalCount + 1,
    },
  };
}

export function removeBlockedUser<T extends GlobalState>(global: T, contactId: string): T {
  global = updateUserBlockedState(global, contactId, false);

  return {
    ...global,
    blocked: {
      ...global.blocked,
      ids: global.blocked.ids.filter((id) => id !== contactId),
      totalCount: global.blocked.totalCount - 1,
    },
  };
}
